import { apiClient } from "./client"

import type { StudentNote, StudentProfileIdentity, StudentProfileOverview } from "@/types"

export interface CreateNotePayload {
  body: string
  /** Optional provenance — which subject the professor was teaching. */
  classroom_id?: string | null
  pinned?: boolean
}

export interface UpdateNotePayload {
  body?: string
  pinned?: boolean
}

/** Only the keys that changed — the backend uses exclude_unset semantics. */
export interface UpdateProfileIdentityPayload {
  name?: string
  email?: string
  matricula?: string
  /** PRD-40 §FR-19 — the survey's keys only; anything else is ignored server-side. */
  persona?: ProfilePersonaPayload
}

// Mirrors core/material_types.py::student_visible_persona_keys (the lists
// the survey asks for). Professor notes are not here: they are the timeline.
export interface ProfilePersonaPayload {
  interests?: string[]
  goals?: string[]
  preferred_context?: string[]
}

export const studentProfilesApi = {
  overview: (profileId: string) =>
    apiClient
      .get<StudentProfileOverview>(`/student-profiles/${profileId}/overview`)
      .then((r) => r.data),

  /** PRD-40 §FR-19 — edit the person; propagates onto every enrollment. */
  updateIdentity: (profileId: string, data: UpdateProfileIdentityPayload) =>
    apiClient
      .patch<StudentProfileIdentity>(`/student-profiles/${profileId}`, data)
      .then((r) => r.data),

  listNotes: (profileId: string) =>
    apiClient.get<StudentNote[]>(`/student-profiles/${profileId}/notes`).then((r) => r.data),

  createNote: (profileId: string, data: CreateNotePayload) =>
    apiClient.post<StudentNote>(`/student-profiles/${profileId}/notes`, data).then((r) => r.data),

  updateNote: (noteId: string, data: UpdateNotePayload) =>
    apiClient.patch<StudentNote>(`/student-notes/${noteId}`, data).then((r) => r.data),

  deleteNote: (noteId: string) =>
    apiClient.delete<{ success: boolean }>(`/student-notes/${noteId}`).then((r) => r.data),

  /** Detach one enrollment onto a person of its own (PRD-40 §FR-12). */
  splitProfile: (studentId: string) =>
    apiClient.post(`/students/${studentId}/split-profile`).then((r) => r.data),
}
